import dayjs from "dayjs";
import { SupplyPreviewPlot } from "components/autoRecipeParameters/SupplyPreviewPlot";
import { Stack } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import isEqual from "fast-deep-equal";
import { useNotifyStatus } from "contexts/status";
import { useTenantTranslation } from "hooks/formatters";
import { useAppDispatch } from "store/store";
import { useTenant } from "hooks/settings";
import {
  AutoRecipeDefaultsRead,
  generatedApi,
  useRevertAutoRecipeDefaultsMutation,
  useUpdateAutoRecipeDefaultsMutation,
} from "store/api/generatedApi";
import { AutoRecipeParametersForm } from "components/autoRecipeParameters/AutoRecipeParametersForm";
import { useAutoRecipeEditor } from "components/autoRecipeParameters/useAutoRecipeEditor";
import { AutoRecipeEditorAlerts } from "components/autoRecipeParameters/AutoRecipeEditorAlerts";
import { useDefaultParametersContext } from "./context";
import { DefaultParameterStatus } from "./DefaultParameterStatus";
import { RevertDefaultsButton } from "./RevertDefaultsButton";

export const AutoRecipeDefaultsEditor = ({
  defaults,
  isActive,
  refresh,
  setConfirmOpen,
  setHasChanges,
}: {
  defaults: AutoRecipeDefaultsRead;
  isActive: boolean;
  refresh: () => Promise<unknown>;
  setConfirmOpen: (open: boolean) => void;
  setHasChanges: (hasChanges: boolean) => void;
}) => {
  const [previewTimestamp] = useState(() => dayjs().toISOString());
  const tenantName = useTenant();
  const dispatch = useAppDispatch();
  const { t } = useTenantTranslation();
  const notify = useNotifyStatus();
  const { setTabActions } = useDefaultParametersContext();
  const editor = useAutoRecipeEditor({ defaults, scope: "defaults" });
  const {
    source,
    draft,
    setDraft,
    discard,
    save,
    isStale,
    isValid,
    isChecking,
  } = editor;
  const [update, { isLoading: isUpdating }] =
    useUpdateAutoRecipeDefaultsMutation();
  const [revert, { isLoading: isReverting }] =
    useRevertAutoRecipeDefaultsMutation();
  const isSaving = isUpdating || isReverting;
  const hasChanges = !isEqual(source.effective.parameters, draft);
  const onSave = useCallback(async () => {
    if (!hasChanges || !isValid || isStale || isSaving) return;
    try {
      await save(async (savedSource, parameters) => {
        await update({
          tenantName,
          autoRecipeDefaultsRequest: {
            context: savedSource.context,
            parameters,
          },
        }).unwrap();
        await refresh();
      });
      setConfirmOpen(false);
    } catch {
      notify({ type: "error", text: t("autoSaveFailed") });
      void refresh();
    }
  }, [
    hasChanges,
    isValid,
    isStale,
    isSaving,
    save,
    update,
    tenantName,
    refresh,
    setConfirmOpen,
    notify,
    t,
  ]);
  const onRevert = async () => {
    if (isSaving || isStale) return;
    try {
      await save(async (savedSource) => {
        const restored = await revert({
          tenantName,
          autoRecipeDefaultsContext: savedSource.context,
        }).unwrap();
        dispatch(
          generatedApi.util.updateQueryData(
            "getAutoRecipeDefaults",
            { tenantName, searchType: savedSource.context.search_type },
            () => restored
          )
        );
      });
      setConfirmOpen(false);
    } catch {
      notify({ type: "error", text: t("autoSaveFailed") });
      void refresh();
    }
  };
  useEffect(() => {
    setHasChanges(hasChanges);
    return () => {
      setHasChanges(false);
    };
  }, [hasChanges, setHasChanges]);
  useEffect(() => {
    if (!isActive) return;
    setTabActions({
      hasChanges,
      isChecking,
      isSaving,
      saveAvailability: isValid && !isStale ? "ready" : "blocked",
      conflicts: [],
      onSave,
      onDiscard: discard,
    });
    return () => {
      setTabActions(null);
    };
  }, [
    setTabActions,
    isActive,
    hasChanges,
    isChecking,
    isSaving,
    isValid,
    isStale,
    onSave,
    discard,
  ]);
  return (
    <Stack gap={2}>
      <Stack direction="row" gap={2} alignItems="center">
        <DefaultParameterStatus
          matchesScrapchefDefaults={isEqual(
            defaults.effective.parameters,
            defaults.scrapchef.parameters
          )}
        />
        <RevertDefaultsButton
          key={JSON.stringify(source.context)}
          disabled={
            isSaving ||
            isStale ||
            (!hasChanges &&
              isEqual(source.effective.parameters, source.scrapchef.parameters))
          }
          warning={t("autoRestoreDefaultsWarning")}
          onRevert={() => {
            void onRevert();
          }}
        />
      </Stack>
      <AutoRecipeEditorAlerts
        editor={editor}
        refresh={refresh}
        disabled={isSaving}
      />
      <AutoRecipeParametersForm
        supplyPreview={
          <SupplyPreviewPlot
            context={source.context}
            parameters={draft}
            scope="defaults"
            referenceTimestamp={previewTimestamp}
            isValid={editor.schemaValidation.valid}
          />
        }
        scope="defaults"
        validation={editor.schemaValidation}
        parameters={draft}
        definition={source.definition}
        onChange={setDraft}
        disabled={isSaving}
      />
    </Stack>
  );
};
